"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  quotesCacheService,
  type CachedQuote,
} from "@/services/quotes-cache-service";
import {
  greeksCacheService,
  type CachedGreeks,
} from "@/services/greeks-cache-service.client";
import { createLogger } from "@/utils/logger";
import type {
  AutomationStreamState,
  AutomationStreamStatus,
} from "@/types/automation";

type RegistryInstrumentType =
  | "stock"
  | "option"
  | "futures"
  | "index"
  | "unknown";

export interface RegistryMarketDataSymbol {
  symbol: string;
  instrumentType?: RegistryInstrumentType;
  streamerSymbol?: string | null;
  underlyingSymbol?: string | null;
}

export type MarketDataStreamStatus = "connecting" | "connected" | "error";

export interface MarketDataTick {
  symbol: string;
  price: number;
  timestamp: number;
  publishedAt?: number;
  source?: string;
  bid?: number;
  ask?: number;
  mid?: number;
}

export interface MarketDataGreeks {
  symbol: string;
  underlying?: string;
  timestamp: number;
  delta?: number;
  gamma?: number;
  theta?: number;
  vega?: number;
  rho?: number;
  iv?: number;
  theoretical?: number;
  source?: string;
}

export interface MarketDataExpectedMove {
  symbol: string;
  expectedMove: number;
  underlyingPrice?: number | null;
  timestamp: number;
  status?: string;
  source?: string;
  window?: { calls?: string[]; puts?: string[] } | null;
}

interface RegistrySourceRequest {
  symbols: RegistryMarketDataSymbol[];
  includeGreeks?: boolean;
  includeExpectedMove?: boolean;
}

interface SymbolRequirement {
  includeGreeks: boolean;
  includeExpectedMove: boolean;
}

export interface MarketDataTelemetry {
  averageLatencyMs: number;
  maxLatencyMs: number;
  fallbackCount: number;
  lastFallbackReason: string | null;
  lastFallbackAt: number | null;
  staleSymbols: Array<{ symbol: string; ageMs: number }>;
  statusSummary: Record<MarketDataStreamStatus, number>;
  serverSnapshot?: ServerTelemetrySnapshot | null;
}

export interface ServerTelemetrySnapshot {
  timestamp: number;
  sseSubscribers: number;
  maxSubscribers: number;
  redisLag: {
    averageMs: number;
    maxMs: number;
    lastMs: number;
    sampleCount: number;
  };
  fallbacks: {
    total: number;
    lastReason: string | null;
    lastAt: number | null;
    byReason: Record<string, number>;
    window: {
      periodMs: number;
      counts: Record<string, number>;
    };
  };
  streamer: {
    status: "idle" | "connected" | "disconnected" | "reconnecting";
    lastConnectedAt: number | null;
    lastDisconnectAt: number | null;
    lastDisconnectReason: string | null;
    lastReconnectBackoffMs: number | null;
    reconnectAttempts: number;
  };
}

interface MarketDataRegistryContextValue {
  registerSource: (key: string, request: RegistrySourceRequest) => void;
  unregisterSource: (key: string) => void;
  registerAutomation: (key: string) => void;
  unregisterAutomation: (key: string) => void;
  ticks: Map<string, MarketDataTick>;
  greeks: Map<string, MarketDataGreeks>;
  expectedMoves: Map<string, MarketDataExpectedMove>;
  status: Map<string, MarketDataStreamStatus>;
  errors: Map<string, string | null>;
  isConnected: boolean;
  getQuote: (symbol: string) => CachedQuote | null;
  getGreeks: (symbol: string) => CachedGreeks | null;
  telemetry: MarketDataTelemetry;
  automationState: AutomationStreamState | null;
  automationStatus: AutomationStreamStatus;
  automationError: string | null;
  automationLastUpdate: Date | null;
}

const logger = createLogger("market-data-registry");

const MarketDataRegistryContext = createContext<
  MarketDataRegistryContextValue | undefined
>(undefined);

const canonicalize = (value: string | null | undefined): string => {
  const trimmed = typeof value === "string" ? value.trim() : value == null ? "" : String(value).trim();
  if (!trimmed) {
    return "";
  }
  const withoutLeadingDot = trimmed.startsWith('.') ? trimmed.slice(1) : trimmed;
  return withoutLeadingDot.toUpperCase();
};

const normalizeSymbolRequest = (
  request: RegistrySourceRequest,
): RegistrySourceRequest => {
  const symbols = request.symbols
    .map((entry) => {
      const primary = canonicalize(entry.symbol ?? entry.streamerSymbol ?? "");
      if (!primary) {
        return null;
      }
      return {
        symbol: primary,
        instrumentType: (entry.instrumentType ?? "unknown") as RegistryInstrumentType,
        streamerSymbol: canonicalize(entry.streamerSymbol),
        underlyingSymbol: canonicalize(entry.underlyingSymbol),
      } as RegistryMarketDataSymbol;
    })
    .filter((entry): entry is RegistryMarketDataSymbol => entry !== null);

  return {
    symbols,
    includeGreeks: !!request.includeGreeks,
    includeExpectedMove: !!request.includeExpectedMove,
  } satisfies RegistrySourceRequest;
};

interface EventEnvelope {
  symbol: string;
  payload: Record<string, unknown>;
}

const toFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

type QuotePayload = Record<string, unknown> & {
  data?: Record<string, unknown>;
};

interface DerivedQuoteFields {
  price?: number;
  bid?: number;
  ask?: number;
  mid?: number;
  last?: number;
  timestamp?: number;
  publishedAt?: number;
}

export const deriveQuoteFields = (payload: QuotePayload): DerivedQuoteFields => {
  const data = payload && typeof payload.data === "object" && payload.data !== null
    ? (payload.data as Record<string, unknown>)
    : {};

  const pick = (...candidates: unknown[]): number | undefined => {
    for (const candidate of candidates) {
      const numeric = toFiniteNumber(candidate);
      if (numeric !== undefined) {
        return numeric;
      }
    }
    return undefined;
  };

  const bid = pick(payload.bid, payload.bidPrice, data.bid, data.bidPrice);
  const ask = pick(payload.ask, payload.askPrice, data.ask, data.askPrice);
  const mid = pick(
    payload.mid,
    payload.midPrice,
    data.mid,
    data.midPrice,
    (bid !== undefined && ask !== undefined) ? (bid + ask) / 2 : undefined,
  );
  const last = pick(payload.last, payload.lastPrice, data.last, data.lastPrice);
  const price = pick(
    payload.price,
    data.price,
    payload.mark,
    payload.markPrice,
    data.mark,
    data.markPrice,
    mid,
    last,
    bid,
    ask,
  );

  const timestamp = pick(payload.timestamp, data.timestamp);
  const publishedAt = pick(payload.publishedAt, data.publishedAt);

  return { price, bid, ask, mid, last, timestamp, publishedAt };
};

type GreeksPayload = Record<string, unknown> & {
  data?: Record<string, unknown>;
  greeks?: Record<string, unknown>;
};

interface DerivedGreeksFields {
  delta?: number;
  gamma?: number;
  theta?: number;
  vega?: number;
  rho?: number;
  iv?: number;
  theoretical?: number;
}

export const deriveGreeksFields = (
  payload: GreeksPayload,
): DerivedGreeksFields => {
  const data =
    payload && typeof payload.data === "object" && payload.data !== null
      ? payload.data
      : payload && typeof payload.greeks === "object" && payload.greeks !== null
        ? payload.greeks
        : {};

  return {
    delta: toFiniteNumber(data.delta),
    gamma: toFiniteNumber(data.gamma),
    theta: toFiniteNumber(data.theta),
    vega: toFiniteNumber(data.vega),
    rho: toFiniteNumber(data.rho),
    iv: toFiniteNumber(
      data.iv ?? data.impliedVolatility ?? data.implied_volatility,
    ),
    theoretical: toFiniteNumber(data.theoretical),
  };
};

const parseEventPayload = (data: unknown): EventEnvelope | null => {
  if (typeof data !== "string") {
    return null;
  }

  const trimmed = data.trim();
  if (!trimmed || trimmed === "undefined" || trimmed === "null") {
    return null;
  }

  try {
    const payload = JSON.parse(trimmed) as Record<string, unknown>;

    const related = payload.relatedSymbol as string | undefined;

    let symbolCandidate: string | undefined;
    const rawSymbol = payload.symbol;
    if (typeof rawSymbol === "string") {
      symbolCandidate = rawSymbol;
    } else if (rawSymbol && typeof rawSymbol === "object") {
      const symbolObject = rawSymbol as {
        streamer?: unknown;
        position?: unknown;
        underlying?: unknown;
      };
      if (typeof symbolObject.streamer === "string" && symbolObject.streamer) {
        symbolCandidate = symbolObject.streamer;
      } else if (
        typeof symbolObject.position === "string" &&
        symbolObject.position
      ) {
        symbolCandidate = symbolObject.position;
      } else if (
        typeof symbolObject.underlying === "string" &&
        symbolObject.underlying
      ) {
        symbolCandidate = symbolObject.underlying;
      }
    }

    let underlyingCandidate: string | undefined;
    if (typeof payload.underlying === "string") {
      underlyingCandidate = payload.underlying;
    } else if (
      payload.underlying &&
      typeof payload.underlying === "object" &&
      typeof (payload.underlying as { symbol?: unknown }).symbol === "string"
    ) {
      underlyingCandidate = (payload.underlying as { symbol?: string }).symbol ?? undefined;
    }

    const resolved = canonicalize(
      symbolCandidate ?? underlyingCandidate ?? related ?? "",
    );
    if (!resolved) {
      return null;
    }
    return { symbol: resolved, payload };
  } catch (error) {
    logger.error("Failed to parse market data payload", error);
    return null;
  }
};

export function MarketDataRegistryProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [sources, setSources] = useState<Map<string, RegistrySourceRequest>>(
    new Map(),
  );

  const [ticks, setTicks] = useState<Map<string, MarketDataTick>>(new Map());
  const [greeks, setGreeks] = useState<Map<string, MarketDataGreeks>>(new Map());
  const [expectedMoves, setExpectedMoves] =
    useState<Map<string, MarketDataExpectedMove>>(new Map());
  const [status, setStatus] =
    useState<Map<string, MarketDataStreamStatus>>(new Map());
  const [errors, setErrors] = useState<Map<string, string | null>>(new Map());
  const [isConnected, setConnected] = useState(false);
  const [telemetry, setTelemetry] = useState<MarketDataTelemetry>({
    averageLatencyMs: 0,
    maxLatencyMs: 0,
    fallbackCount: 0,
    lastFallbackReason: null,
    lastFallbackAt: null,
    staleSymbols: [],
    statusSummary: {
      connecting: 0,
      connected: 0,
      error: 0,
    },
    serverSnapshot: null,
  });
  const [automationSubscribers, setAutomationSubscribers] =
    useState<Map<string, true>>(new Map());
  const [automationState, setAutomationState] =
    useState<AutomationStreamState | null>(null);
  const [automationStatus, setAutomationStatus] =
    useState<AutomationStreamStatus>("idle");
  const [automationError, setAutomationError] = useState<string | null>(null);
  const [automationLastUpdate, setAutomationLastUpdate] = useState<Date | null>(
    null,
  );

  const requirementsRef = useRef<Map<string, SymbolRequirement>>(new Map());
  const eventSourceRef = useRef<EventSource | null>(null);
  const teardownRef = useRef<(() => void) | null>(null);
  const currentKeyRef = useRef<string>("");
  const lastEventAtRef = useRef<Map<string, number>>(new Map());
  const latencyStatsRef = useRef<{ count: number; total: number; max: number }>({
    count: 0,
    total: 0,
    max: 0,
  });
  const fallbackRef = useRef<{
    count: number;
    reason: string | null;
    at: number | null;
  }>({
    count: 0,
    reason: null,
    at: null,
  });
  const statusRef = useRef<Map<string, MarketDataStreamStatus>>(new Map());
  const serverTelemetryRef = useRef<ServerTelemetrySnapshot | null>(null);

  // Frame-coalescing buffers: incoming SSE messages are buffered here
  // (last-write-wins per symbol) and drained to React state at most once per
  // animation frame, instead of one setState per message.
  const pendingTicksRef = useRef<Map<string, MarketDataTick>>(new Map());
  const pendingGreeksRef = useRef<Map<string, MarketDataGreeks>>(new Map());
  const pendingExpectedMovesRef = useRef<Map<string, MarketDataExpectedMove>>(
    new Map(),
  );
  const flushHandleRef = useRef<{
    raf: number | null;
    timer: ReturnType<typeof setTimeout> | null;
  }>({ raf: null, timer: null });

  const flushPendingUpdates = useCallback(() => {
    const handles = flushHandleRef.current;
    if (handles.raf !== null) {
      cancelAnimationFrame(handles.raf);
      handles.raf = null;
    }
    if (handles.timer !== null) {
      clearTimeout(handles.timer);
      handles.timer = null;
    }

    const pendingTicks = pendingTicksRef.current;
    if (pendingTicks.size > 0) {
      pendingTicksRef.current = new Map();
      setTicks((prev) => {
        const next = new Map(prev);
        pendingTicks.forEach((tick, symbol) => next.set(symbol, tick));
        return next;
      });
    }

    const pendingGreeks = pendingGreeksRef.current;
    if (pendingGreeks.size > 0) {
      pendingGreeksRef.current = new Map();
      setGreeks((prev) => {
        const next = new Map(prev);
        pendingGreeks.forEach((entry, symbol) => next.set(symbol, entry));
        return next;
      });
    }

    const pendingExpectedMoves = pendingExpectedMovesRef.current;
    if (pendingExpectedMoves.size > 0) {
      pendingExpectedMovesRef.current = new Map();
      setExpectedMoves((prev) => {
        const next = new Map(prev);
        pendingExpectedMoves.forEach((entry, symbol) => next.set(symbol, entry));
        return next;
      });
    }
  }, []);

  const scheduleFlush = useCallback(() => {
    const handles = flushHandleRef.current;
    if (handles.raf !== null || handles.timer !== null) {
      return;
    }
    // rAF does not fire in hidden tabs; fall back to a timer so quotes keep
    // updating for anything reading them while the tab is backgrounded.
    if (
      typeof document !== "undefined" &&
      document.visibilityState === "hidden"
    ) {
      handles.timer = setTimeout(() => {
        handles.timer = null;
        flushPendingUpdates();
      }, 250);
    } else if (typeof requestAnimationFrame === "function") {
      handles.raf = requestAnimationFrame(() => {
        handles.raf = null;
        flushPendingUpdates();
      });
    } else {
      handles.timer = setTimeout(() => {
        handles.timer = null;
        flushPendingUpdates();
      }, 16);
    }
  }, [flushPendingUpdates]);

  useEffect(() => {
    // If the tab goes hidden while a rAF flush is scheduled, that rAF will not
    // fire until the tab is visible again — flush immediately instead.
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushPendingUpdates();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [flushPendingUpdates]);

  useEffect(() => {
    const handles = flushHandleRef.current;
    return () => {
      if (handles.raf !== null) {
        cancelAnimationFrame(handles.raf);
        handles.raf = null;
      }
      if (handles.timer !== null) {
        clearTimeout(handles.timer);
        handles.timer = null;
      }
    };
  }, []);

  const registerSource = useCallback(
    (key: string, request: RegistrySourceRequest) => {
      setSources((prev) => {
        const next = new Map(prev);
        next.set(key, normalizeSymbolRequest(request));
        return next;
      });
    },
    [],
  );

  const unregisterSource = useCallback((key: string) => {
    setSources((prev) => {
      if (!prev.has(key)) {
        return prev;
      }
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const registerAutomation = useCallback((key: string) => {
    setAutomationSubscribers((prev) => {
      const next = new Map(prev);
      next.set(key, true);
      return next;
    });
  }, []);

  const unregisterAutomation = useCallback((key: string) => {
    setAutomationSubscribers((prev) => {
      if (!prev.has(key)) {
        return prev;
      }
      const next = new Map(prev);
      next.delete(key);
      if (next.size === 0) {
        setAutomationStatus("idle");
        setAutomationState(null);
        setAutomationError(null);
        setAutomationLastUpdate(null);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    const aggregated = new Map<string, SymbolRequirement>();

    sources.forEach((request) => {
      request.symbols.forEach((symbol) => {
        const key = canonicalize(symbol.symbol);
        if (!key) {
          return;
        }
        const existing = aggregated.get(key) ?? {
          includeGreeks: false,
          includeExpectedMove: false,
        };
        aggregated.set(key, {
          includeGreeks: existing.includeGreeks || !!request.includeGreeks,
          includeExpectedMove:
            existing.includeExpectedMove || !!request.includeExpectedMove,
        });
      });
    });

    requirementsRef.current = aggregated;

    const sortedSymbols = Array.from(aggregated.keys()).sort();
    const types: string[] = [];
    if (sortedSymbols.length > 0) {
      types.push("price");
    }
    if (Array.from(aggregated.values()).some((r) => r.includeGreeks)) {
      types.push("greeks");
    }
    if (Array.from(aggregated.values()).some((r) => r.includeExpectedMove)) {
      types.push("expectedMove");
    }

    const shouldRequestAutomation = automationSubscribers.size > 0;
    if (shouldRequestAutomation) {
      types.push("automation");
    }

    const params = new URLSearchParams();
    sortedSymbols.forEach((symbol) => params.append("symbol", symbol));
    if (types.length > 0) {
      params.set("types", types.join(","));
    }
    const key = `${sortedSymbols.join(",")}|${types.join(",")}`;

    const teardown = () => {
      if (teardownRef.current) {
        try {
          teardownRef.current();
        } catch (error) {
          logger.error("Failed to tear down market data stream", error);
        }
        teardownRef.current = null;
      }
      if (eventSourceRef.current) {
        try {
          eventSourceRef.current.close();
        } catch (error) {
          logger.warn("EventSource close error", error);
        }
        eventSourceRef.current = null;
      }
      currentKeyRef.current = "";
    };

    if (sortedSymbols.length === 0 && !shouldRequestAutomation) {
      teardown();
      setConnected(false);
      pendingTicksRef.current = new Map();
      pendingGreeksRef.current = new Map();
      pendingExpectedMovesRef.current = new Map();
      setTicks(new Map());
      setGreeks(new Map());
      setExpectedMoves(new Map());
      setStatus(new Map());
      setErrors(new Map());
      return;
    }

    if (currentKeyRef.current === key && eventSourceRef.current) {
      return;
    }

    teardown();

    if (shouldRequestAutomation) {
      setAutomationStatus("connecting");
      setAutomationError(null);
    }

    const queryString = params.toString();
    if (!queryString) {
      return;
    }

    const eventSource = new EventSource(
      `/api/market-data/stream?${queryString}`,
    );
    eventSourceRef.current = eventSource;
    currentKeyRef.current = key;

    const updateStatus = (
      symbol: string,
      nextStatus: MarketDataStreamStatus,
      errorMessage: string | null = null,
    ) => {
      setStatus((prev) => {
        if (prev.get(symbol) === nextStatus) {
          return prev;
        }
        const next = new Map(prev);
        next.set(symbol, nextStatus);
        return next;
      });
      setErrors((prev) => {
        if (prev.has(symbol) && prev.get(symbol) === errorMessage) {
          return prev;
        }
        const next = new Map(prev);
        next.set(symbol, errorMessage);
        return next;
      });
    };

    const handlePrice = (event: MessageEvent) => {
      const envelope = parseEventPayload(event.data);
      if (!envelope) {
        return;
      }
      const requirements = requirementsRef.current.get(envelope.symbol);
      if (!requirements) {
        return;
      }

      const { price, bid, ask, mid, last, timestamp, publishedAt } = deriveQuoteFields(envelope.payload as QuotePayload);
      if (
        price === undefined &&
        bid === undefined &&
        ask === undefined &&
        mid === undefined &&
        last === undefined
      ) {
        return;
      }

      const resolvedMid = mid ?? (bid !== undefined && ask !== undefined ? (bid + ask) / 2 : undefined);
      const resolvedPrice = price ?? resolvedMid ?? last ?? bid ?? ask;
      if (resolvedPrice === undefined) {
        return;
      }

      const tickTimestamp = timestamp ?? Date.now();
      const tickPublishedAt = publishedAt ?? tickTimestamp;

      const tick: MarketDataTick = {
        symbol: envelope.symbol,
        price: resolvedPrice,
        timestamp: tickTimestamp,
        publishedAt: tickPublishedAt,
        bid: bid,
        ask: ask,
        mid: resolvedMid,
        source:
          typeof envelope.payload.source === "string"
            ? (envelope.payload.source as string)
            : undefined,
      };

      pendingTicksRef.current.set(envelope.symbol, tick);
      scheduleFlush();

      const now = Date.now();
      lastEventAtRef.current.set(envelope.symbol, now);

      const published = tick.publishedAt ?? tick.timestamp;
      const latency = Math.max(0, now - published);
      const stats = latencyStatsRef.current;
      stats.count += 1;
      stats.total += latency;
      if (latency > stats.max) {
        stats.max = latency;
      }

      try {
        quotesCacheService.processQuoteUpdate({
          eventType: "QUOTE",
          eventSymbol: envelope.symbol,
          bidPrice: tick.bid,
          askPrice: tick.ask,
          lastPrice: tick.price,
          timestamp: tick.timestamp,
          sourceTimestamp: tick.publishedAt,
        });
      } catch (error) {
        logger.error("Failed to update quote cache", error);
      }

      updateStatus(envelope.symbol, "connected", null);
      setConnected(true);
    };

    const handleGreeks = (event: MessageEvent) => {
      const envelope = parseEventPayload(event.data);
      if (!envelope) {
        return;
      }
      const requirements = requirementsRef.current.get(envelope.symbol);
      if (!requirements?.includeGreeks) {
        return;
      }

      const derivedGreeks = deriveGreeksFields(envelope.payload as GreeksPayload);

      const entry: MarketDataGreeks = {
        symbol: envelope.symbol,
        underlying:
          typeof envelope.payload.underlying === "string"
            ? (envelope.payload.underlying as string)
            : undefined,
        timestamp:
          typeof envelope.payload.timestamp === "number"
            ? (envelope.payload.timestamp as number)
            : Date.now(),
        delta: derivedGreeks.delta,
        gamma: derivedGreeks.gamma,
        theta: derivedGreeks.theta,
        vega: derivedGreeks.vega,
        rho: derivedGreeks.rho,
        iv: derivedGreeks.iv,
        theoretical: derivedGreeks.theoretical,
        source:
          typeof envelope.payload.source === "string"
            ? (envelope.payload.source as string)
            : typeof envelope.payload.type === "string"
              ? (envelope.payload.type as string)
              : undefined,
      };

      pendingGreeksRef.current.set(envelope.symbol, entry);
      scheduleFlush();

      lastEventAtRef.current.set(envelope.symbol, Date.now());

      try {
        greeksCacheService.processGreeksUpdate({
          eventType: "GREEKS",
          eventSymbol: envelope.symbol,
          delta: entry.delta,
          gamma: entry.gamma,
          theta: entry.theta,
          vega: entry.vega,
          rho: entry.rho,
          timestamp: entry.timestamp,
        });
      } catch (error) {
        logger.error("Failed to update greeks cache", error);
      }
    };

    const handleExpectedMove = (event: MessageEvent) => {
      const envelope = parseEventPayload(event.data);
      if (!envelope) {
        return;
      }
      const requirements = requirementsRef.current.get(envelope.symbol);
      if (!requirements?.includeExpectedMove) {
        return;
      }

      if (typeof envelope.payload.expectedMove !== "number") {
        return;
      }

      const entry: MarketDataExpectedMove = {
        symbol: envelope.symbol,
        expectedMove: envelope.payload.expectedMove as number,
        underlyingPrice:
          typeof envelope.payload.underlyingPrice === "number"
            ? (envelope.payload.underlyingPrice as number)
            : null,
        timestamp:
          typeof envelope.payload.timestamp === "number"
            ? (envelope.payload.timestamp as number)
            : Date.now(),
        status:
          typeof envelope.payload.status === "string"
            ? (envelope.payload.status as string)
            : undefined,
        source:
          typeof envelope.payload.source === "string"
            ? (envelope.payload.source as string)
            : undefined,
        window: (envelope.payload.window as MarketDataExpectedMove["window"]) ?? null,
      };

      pendingExpectedMovesRef.current.set(envelope.symbol, entry);
      scheduleFlush();

      lastEventAtRef.current.set(envelope.symbol, Date.now());
    };

    const handleInfo = (event: MessageEvent) => {
      const envelope = parseEventPayload(event.data);
      if (!envelope) {
        return;
      }
      const message =
        typeof envelope.payload.message === "string"
          ? (envelope.payload.message as string)
          : null;
      if (message) {
        updateStatus(envelope.symbol, "error", message);
        if (/fallback|rest/i.test(message)) {
          fallbackRef.current = {
            count: fallbackRef.current.count + 1,
            reason: message,
            at: Date.now(),
          };
        }
      }
    };

    const handleAutomation = (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data) as Record<string, unknown>;
        const raw = (payload.payload ?? payload) as Record<string, unknown>;

        const normalizeBoolean = (value: unknown): boolean =>
          value === true || value === "true" || value === 1;

        const normalizeString = (value: unknown): string | null =>
          typeof value === "string" && value.trim().length ? value.trim() : null;

        const normalizeDate = (value: unknown): Date | null => {
          if (value instanceof Date && !Number.isNaN(value.getTime())) {
            return value;
          }
          if (typeof value === "number" && Number.isFinite(value)) {
            const parsed = new Date(value);
            return Number.isNaN(parsed.getTime()) ? null : parsed;
          }
          if (typeof value === "string" && value.trim().length) {
            const parsed = new Date(value);
            return Number.isNaN(parsed.getTime()) ? null : parsed;
          }
          return null;
        };

        const identifierRaw = raw.id ?? payload.id;
        const identifier = normalizeString(identifierRaw) ??
          (typeof identifierRaw === "number" ? String(identifierRaw) : "automation");

        const state: AutomationStreamState = {
          id: identifier,
          isActive: normalizeBoolean(raw.isActive ?? raw["is_active"]),
          isPaused: normalizeBoolean(raw.isPaused ?? raw["is_paused"]),
          pauseReason: normalizeString(raw.pauseReason ?? raw["pause_reason"]),
          lastExecutionTime: normalizeDate(raw.lastExecutionTime ?? raw["last_execution_time"]),
          nextScheduledTime: normalizeDate(raw.nextScheduledTime ?? raw["next_scheduled_time"]),
          updatedAt: normalizeDate(raw.updatedAt ?? raw["updated_at"]),
        };

        setAutomationState(state);
        setAutomationStatus("connected");
        setAutomationError(null);
        const publishedAt = normalizeDate(payload.publishedAt);
        setAutomationLastUpdate(publishedAt ?? new Date());
      } catch (error) {
        logger.error("Failed to process automation event", error);
        setAutomationStatus("error");
        setAutomationError("Failed to parse automation update");
      }
    };

    const handleServerTelemetry = (event: MessageEvent) => {
      try {
        const snapshot = JSON.parse(event.data) as ServerTelemetrySnapshot;
        serverTelemetryRef.current = snapshot;
      } catch (error) {
        logger.error("Failed to parse server telemetry", error);
      }
    };

    const handleError = (event: MessageEvent) => {
      const envelope = parseEventPayload(event.data);
      const message =
        envelope?.payload?.message && typeof envelope.payload.message === "string"
          ? (envelope.payload.message as string)
          : "Market data stream error";
      aggregated.forEach((_, symbol) => {
        updateStatus(symbol, "error", message);
      });
      setConnected(false);
      fallbackRef.current = {
        count: fallbackRef.current.count + 1,
        reason: message,
        at: Date.now(),
      };
      if (shouldRequestAutomation) {
        setAutomationStatus("error");
        setAutomationError(message);
      }
    };

    eventSource.addEventListener("price", handlePrice);
    eventSource.addEventListener("greeks", handleGreeks);
    eventSource.addEventListener("expectedMove", handleExpectedMove);
    eventSource.addEventListener("info", handleInfo);
    if (shouldRequestAutomation) {
      eventSource.addEventListener(
        "automation",
        handleAutomation as EventListener,
      );
    }
    eventSource.addEventListener("telemetry", handleServerTelemetry as EventListener);
    eventSource.addEventListener("error", handleError);

    eventSource.onopen = () => {
      aggregated.forEach((_, symbol) => {
        updateStatus(symbol, "connected", null);
      });
      setConnected(true);
      if (shouldRequestAutomation) {
        setAutomationStatus("connected");
        setAutomationError(null);
      }
    };

    eventSource.onerror = () => {
      aggregated.forEach((_, symbol) => {
        updateStatus(symbol, "error", "Market data stream disconnected");
      });
      setConnected(false);
      if (shouldRequestAutomation) {
        setAutomationStatus("error");
        setAutomationError("Automation stream disconnected");
      }
    };

    const localTeardown = () => {
      eventSource.removeEventListener("price", handlePrice);
      eventSource.removeEventListener("greeks", handleGreeks);
      eventSource.removeEventListener("expectedMove", handleExpectedMove);
      eventSource.removeEventListener("info", handleInfo);
      if (shouldRequestAutomation) {
        eventSource.removeEventListener(
          "automation",
          handleAutomation as EventListener,
        );
      }
      eventSource.removeEventListener(
        "telemetry",
        handleServerTelemetry as EventListener,
      );
      eventSource.removeEventListener("error", handleError);
      try {
        eventSource.close();
      } catch (error) {
        logger.warn("Error while closing EventSource", error);
      }
    };

    teardownRef.current = localTeardown;

    return () => {
      localTeardown();
      teardownRef.current = null;
      eventSourceRef.current = null;
      currentKeyRef.current = "";
    };
  }, [sources, automationSubscribers, scheduleFlush]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const staleSymbols = Array.from(lastEventAtRef.current.entries())
        .map(([symbol, timestamp]) => ({ symbol, ageMs: now - timestamp }))
        .filter((entry) => entry.ageMs >= 0)
        .sort((a, b) => b.ageMs - a.ageMs)
        .slice(0, 20);

      const stats = latencyStatsRef.current;
      const fallback = fallbackRef.current;

      const statusSummary: Record<MarketDataStreamStatus, number> = {
        connecting: 0,
        connected: 0,
        error: 0,
      };
      statusRef.current.forEach((value) => {
        statusSummary[value] += 1;
      });

      setTelemetry({
        averageLatencyMs: stats.count === 0 ? 0 : stats.total / stats.count,
        maxLatencyMs: stats.max,
        fallbackCount: fallback.count,
        lastFallbackReason: fallback.reason,
        lastFallbackAt: fallback.at,
        staleSymbols,
        statusSummary,
        serverSnapshot: serverTelemetryRef.current,
      });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const contextValue = useMemo<MarketDataRegistryContextValue>(() => {
    const getQuote = (symbol: string): CachedQuote | null => {
      const normalized = canonicalize(symbol);
      if (!normalized) {
        return null;
      }
      return quotesCacheService.getQuote(normalized);
    };

    const getGreeks = (symbol: string): CachedGreeks | null => {
      const normalized = canonicalize(symbol);
      if (!normalized) {
        return null;
      }
      return greeksCacheService.getGreeks(normalized);
    };

    return {
      registerSource,
      unregisterSource,
      registerAutomation,
      unregisterAutomation,
      ticks,
      greeks,
      expectedMoves,
      status,
      errors,
      isConnected,
      getQuote,
      getGreeks,
      telemetry,
      automationState,
      automationStatus,
      automationError,
      automationLastUpdate,
    };
  }, [
    registerSource,
    unregisterSource,
    registerAutomation,
    unregisterAutomation,
    ticks,
    greeks,
    expectedMoves,
    status,
    errors,
    isConnected,
    telemetry,
    automationState,
    automationStatus,
    automationError,
    automationLastUpdate,
  ]);

  return (
    <MarketDataRegistryContext.Provider value={contextValue}>
      {children}
    </MarketDataRegistryContext.Provider>
  );
}

export function useMarketDataRegistry(): MarketDataRegistryContextValue {
  const context = useContext(MarketDataRegistryContext);
  if (!context) {
    throw new Error(
      "useMarketDataRegistry must be used within a MarketDataRegistryProvider",
    );
  }
  return context;
}
